/**
 * PodTube UI - Renders cards, filter controls, bottom player, modals and animations
 */

import { store } from "./store.js";
import { player } from "./player.js";

export const SHORT_MAX_SECONDS = 90;

export function formatDuration(seconds) {
  if (!seconds || isNaN(seconds) || seconds <= 0) return "";
  const s = Math.floor(seconds);
  const hrs = Math.floor(s / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;

  if (hrs > 0) {
    return `${hrs}h ${mins.toString().padStart(2, "0")}m`;
  }
  if (mins > 0) {
    return `${mins} min`;
  }
  return `${secs}s`;
}

export function formatTimeAgo(isoDate) {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return "";
  const now = new Date();
  const diffSec = Math.floor((now - date) / 1000);

  if (diffSec < 60) return "Ahora";
  if (diffSec < 3600) return `Hace ${Math.floor(diffSec / 60)} min`;
  if (diffSec < 86400) return `Hace ${Math.floor(diffSec / 3600)} h`;
  const diffDays = Math.floor(diffSec / 86400);
  if (diffDays === 1) return "Ayer";
  if (diffDays < 7) return `Hace ${diffDays} días`;
  if (diffDays < 30) return `Hace ${Math.floor(diffDays / 7)} sem`;
  if (date.getFullYear() !== now.getFullYear()) {
    return date.toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });
  }
  return date.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
}

export function showToast(message, type = "info") {
  const container = document.getElementById("toast-container");
  if (!container) return;

  const toast = document.createElement("div");
  toast.className = `toast toast-${type}`;
  toast.innerHTML = `
    <span class="toast-icon">${type === "success" ? "✓" : type === "error" ? "✕" : "ℹ"}</span>
    <span class="toast-msg">${message}</span>
  `;
  container.appendChild(toast);

  setTimeout(() => toast.classList.add("visible"), 10);
  setTimeout(() => {
    toast.classList.remove("visible");
    setTimeout(() => toast.remove(), 300);
  }, 3200);
}

/**
 * Render channel filter chips
 */
export function renderChannelChips(channels, activeId, onSelect) {
  const container = document.getElementById("channel-chips");
  if (!container) return;

  const allChip = document.createElement("button");
  allChip.className = `channel-chip ${activeId === "all" ? "active" : ""}`;
  allChip.innerHTML = `<span class="chip-label">Todos los canales</span>`;
  allChip.onclick = () => onSelect("all");

  container.innerHTML = "";
  container.appendChild(allChip);

  channels
    .filter((c) => c.enabled !== false)
    .forEach((channel) => {
      const chip = document.createElement("button");
      chip.className = `channel-chip ${activeId === channel.id ? "active" : ""}`;
      chip.style.setProperty("--channel-accent", channel.color || "#6366f1");
      chip.innerHTML = `
      <span class="chip-avatar-dot" style="background-color: ${channel.color || "#6366f1"}"></span>
      <span class="chip-label">${channel.title}</span>
    `;
      chip.onclick = () => onSelect(channel.id);
      container.appendChild(chip);
    });

  const addChip = document.createElement("button");
  addChip.className = "channel-chip channel-chip-add";
  addChip.innerHTML = `<span class="chip-plus">+</span> <span class="chip-label">Fuentes (${channels.filter((c) => c.enabled !== false).length})</span>`;
  addChip.onclick = () => {
    document.getElementById("btn-open-channels")?.click();
  };
  container.appendChild(addChip);
}

/**
 * Filter items according to active settings
 */
export function filterItems(items, settings, searchQuery = "") {
  return items.filter((item) => {
    // Exclude disabled / paused sources
    if (!store.isSubscriptionEnabled(item.channelId)) {
      return false;
    }
    // Channel filter
    if (settings.activeChannelId && settings.activeChannelId !== "all") {
      const isRss = item.channelId?.startsWith("rss:") || item.channelId?.startsWith("http");
      const norm = (id) =>
        id
          ? isRss || id.startsWith("rss:") || id.startsWith("http")
            ? id
            : id.startsWith("UC")
              ? id
              : `UC${id}`
          : "";
      if (norm(item.channelId) !== norm(settings.activeChannelId)) {
        return false;
      }
    }

    // Per-channel duration filter (minDuration / maxDuration in minutes)
    const chDurFilter = store.getChannelDurationFilter(item.channelId);
    if (chDurFilter.minMin !== null || chDurFilter.maxMin !== null) {
      const dur = item.duration || store.getVideoDuration(item.videoId);
      if (dur) {
        const durMin = dur / 60;
        if (chDurFilter.minMin !== null && durMin < chDurFilter.minMin) return false;
        if (chDurFilter.maxMin !== null && durMin > chDurFilter.maxMin) return false;
      }
      // If duration unknown, let it through — it will be filtered once enriched
    }

    // Listened / status filter
    const isListened = store.isListened(item.videoId);
    if (settings.statusFilter === "unheard" && isListened) {
      return false;
    }
    if (settings.statusFilter === "listened" && !isListened) {
      return false;
    }
    if (settings.statusFilter === "favorites" && !store.isFavorite(item.videoId)) {
      return false;
    }

    // Hide shorts filter
    if (settings.hideShorts) {
      if (item.isShort) return false;
      const dur = item.duration || store.getVideoDuration(item.videoId);
      if (dur && dur < SHORT_MAX_SECONDS) return false;
    }

    // Duration filter
    const dur = item.duration || store.getVideoDuration(item.videoId);
    if (settings.durationFilter === "long") {
      if (dur && dur < 1200) return false; // < 20 min
    } else if (settings.durationFilter === "medium") {
      if (dur && (dur < 600 || dur >= 1200)) return false; // 10-20 min
    } else if (settings.durationFilter === "short") {
      if (dur && dur >= 600) return false; // < 10 min
    }

    // Date / Recency filter
    if (settings.dateFilter && settings.dateFilter !== "all") {
      const rawDate = item.published || item.isoDate;
      if (rawDate) {
        const pubTime = new Date(rawDate).getTime();
        if (!isNaN(pubTime)) {
          const diffMs = Date.now() - pubTime;
          if (settings.dateFilter === "24h" && diffMs > 24 * 3600 * 1000) return false;
          if (settings.dateFilter === "7d" && diffMs > 7 * 24 * 3600 * 1000) return false;
          if (settings.dateFilter === "30d" && diffMs > 30 * 24 * 3600 * 1000) return false;
          if (settings.dateFilter === "90d" && diffMs > 90 * 24 * 3600 * 1000) return false;
        }
      }
    }

    // Tag filter
    if (settings.tagFilter && settings.tagFilter !== "all") {
      const tags = store.getTags(item.videoId);
      if (!tags.includes(settings.tagFilter)) return false;
    }

    // Search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchTitle = item.title.toLowerCase().includes(q);
      const matchChannel = (item.channelTitle || "").toLowerCase().includes(q);
      const matchDesc = (item.description || "").toLowerCase().includes(q);
      const matchTags = store.getTags(item.videoId).some((t) => t.toLowerCase().includes(q));
      if (!matchTitle && !matchChannel && !matchDesc && !matchTags) return false;
    }

    return true;
  });
}

/**
 * Render grid of episode cards
 */
export function renderEpisodeGrid(
  items,
  currentVideoId = null,
  onOpenTags = null,
  onLoadOlder = null
) {
  const container = document.getElementById("episodes-grid");
  const countEl = document.getElementById("episodes-count");
  if (!container) return;

  if (countEl) {
    countEl.textContent = `${items.length} episodio${items.length === 1 ? "" : "s"}`;
  }

  if (items.length === 0) {
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-icon">🎧</div>
        <h3>No hay episodios con estos filtros</h3>
        <p>Prueba a buscar vídeos más antiguos en tus canales o restablecer los ajustes de filtrado.</p>
        <div class="empty-state-actions">
          <button class="btn btn-primary btn-load-more" id="btn-load-older-empty">
            <span class="btn-icon">⏳</span> Cargar vídeos más antiguos
          </button>
          <button class="btn btn-secondary" id="btn-reset-filters">Restablecer filtros</button>
        </div>
      </div>
    `;
    const loadOlderEmptyBtn = document.getElementById("btn-load-older-empty");
    if (loadOlderEmptyBtn && typeof onLoadOlder === "function") {
      loadOlderEmptyBtn.onclick = () => onLoadOlder(loadOlderEmptyBtn);
    }
    const resetBtn = document.getElementById("btn-reset-filters");
    if (resetBtn) {
      resetBtn.onclick = () => {
        store.updateSettings({
          statusFilter: "all",
          durationFilter: "all",
          dateFilter: "all",
          tagFilter: "all",
          hideShorts: false,
          activeChannelId: "all",
        });
      };
    }
    return;
  }

  container.innerHTML = "";

  items.forEach((item) => {
    const isPlaying = currentVideoId === item.videoId && player.isPlaying;
    const isCurrent = currentVideoId === item.videoId;
    const isListened = store.isListened(item.videoId);
    const isFav = store.isFavorite(item.videoId);
    const duration = item.duration || store.getVideoDuration(item.videoId);
    const durationLabel = formatDuration(duration);
    const isShort = Boolean(item.isShort || (duration && duration < SHORT_MAX_SECONDS));
    const tags = store.getTags(item.videoId);

    const card = document.createElement("article");
    card.className = `episode-card ${isCurrent ? "is-current" : ""} ${isListened ? "is-listened" : ""}`;
    card.id = `card-${item.videoId}`;

    // Channel color lookup
    const isItemRss = item.channelId?.startsWith("rss:") || item.channelId?.startsWith("http");
    const norm = (id) =>
      id
        ? isItemRss || id.startsWith("rss:") || id.startsWith("http")
          ? id
          : id.startsWith("UC")
            ? id
            : `UC${id}`
        : "";
    const channel = store.getSubscriptions().find((c) => norm(c.id) === norm(item.channelId));
    const isSubstack = item.sourceType === "substack" || item.channelId?.includes("substack");
    const isPodcast = Boolean(item.audioUrl || item.sourceType === "rss" || isSubstack);
    const channelColor =
      channel?.color || (isSubstack ? "#ff6719" : isPodcast ? "#8b5cf6" : "#6366f1");
    const channelName = channel?.title || item.channelTitle || item.author;

    const displayDuration = durationLabel
      ? durationLabel
      : isShort
        ? "⚡ Short"
        : isPodcast
          ? "🎙️ Podcast"
          : "🎙️ Episodio";

    const sourceBadge = isSubstack
      ? `<span class="badge badge-source-substack">Substack</span>`
      : isPodcast
        ? `<span class="badge badge-source-rss">Podcast</span>`
        : "";

    const extUrl =
      item.link || (item.videoId ? `https://www.youtube.com/watch?v=${item.videoId}` : "#");
    const extTitle = isPodcast
      ? "Abrir publicación / podcast original"
      : "Abrir en YouTube / Brave";

    card.innerHTML = `
      <div class="card-thumb-wrapper">
        <img class="card-thumbnail" src="${item.thumbnail}" alt="${item.title}" loading="lazy" />
        <div class="card-overlay-scrim"></div>
        
        <span class="badge badge-duration ${isShort ? "badge-short" : ""}" id="badge-dur-${item.videoId}">${displayDuration}</span>
        ${sourceBadge}
        
        <button class="btn-play-overlay" aria-label="Reproducir episodio">
          <svg viewBox="0 0 24 24" class="play-icon"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
        </button>

        ${
          isPlaying
            ? `
          <div class="card-equalizer">
            <span class="eq-bar"></span><span class="eq-bar"></span><span class="eq-bar"></span>
          </div>
        `
            : ""
        }
      </div>

      <div class="card-content">
        <div class="card-meta-top">
          <span class="card-channel" style="color: ${channelColor};">
            <span class="channel-dot" style="background: ${channelColor};"></span>
            ${channelName}
          </span>
          <div class="card-meta-right">
            <span class="card-duration-tag ${isShort ? "is-short" : ""}" id="tag-dur-${item.videoId}">⏱️ ${durationLabel || (isShort ? "Short" : isPodcast ? "Audio" : "Episodio")}</span>
            <span class="card-meta-separator">•</span>
            <span class="card-date">${formatTimeAgo(item.published)}</span>
          </div>
        </div>

        <h3 class="card-title" title="${item.title}">${item.title}</h3>

        ${item.description ? `<p class="card-description">${item.description.slice(0, 110)}...</p>` : ""}

        <div class="card-tags-line" id="tags-row-${item.videoId}">
          ${tags
            .map(
              (t) => `
            <span class="badge-tag" data-tag="${t}" title="Filtrar por ${t}">${t}</span>
          `
            )
            .join("")}
          <button class="card-btn-tag" data-video-id="${item.videoId}" title="Añadir o editar etiquetas">
            <svg viewBox="0 0 24 24" class="icon"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line></svg>
            <span>${tags.length > 0 ? "Etiquetas" : "+ Etiqueta"}</span>
          </button>
        </div>

        <div class="card-footer">
          <button class="card-btn btn-toggle-listened ${isListened ? "active" : ""}" title="${isListened ? "Marcar como no escuchado" : "Marcar como escuchado"}">
            <svg viewBox="0 0 24 24" class="icon"><path d="M20 6L9 17l-5-5"></path></svg>
            <span>${isListened ? "Escuchado" : "Marcar"}</span>
          </button>

          <button class="card-btn btn-toggle-fav ${isFav ? "active" : ""}" title="${isFav ? "Quitar de favoritos" : "Guardar en favoritos"}">
            <svg viewBox="0 0 24 24" class="icon"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
          </button>

          <a href="${extUrl}" target="_blank" rel="noopener noreferrer" class="card-btn btn-brave-external" title="${extTitle}">
            <svg viewBox="0 0 24 24" class="icon"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
          </a>
        </div>
      </div>
    `;

    const playTrigger = card.querySelector(".btn-play-overlay");
    playTrigger.onclick = () => {
      if (player.currentVideo && player.currentVideo.videoId === item.videoId) {
        player.togglePlayPause();
      } else {
        player.playVideo(item);
      }
    };

    const listenedBtn = card.querySelector(".btn-toggle-listened");
    listenedBtn.onclick = (e) => {
      e.stopPropagation();
      const nowListened = store.toggleListened(item.videoId, duration);
      showToast(
        nowListened ? "Episodio marcado como escuchado ✓" : "Marcado como pendiente",
        "success"
      );
    };

    const favBtn = card.querySelector(".btn-toggle-fav");
    favBtn.onclick = (e) => {
      e.stopPropagation();
      store.toggleFavorite(item.videoId);
      showToast(
        store.isFavorite(item.videoId) ? "Guardado en favoritos ★" : "Eliminado de favoritos",
        "info"
      );
    };

    const tagBtn = card.querySelector(".card-btn-tag");
    if (tagBtn) {
      tagBtn.onclick = (e) => {
        e.stopPropagation();
        if (typeof onOpenTags === "function") {
          onOpenTags(item.videoId, item.title);
        }
      };
    }

    card.querySelectorAll(".badge-tag").forEach((badge) => {
      badge.onclick = (e) => {
        e.stopPropagation();
        const tag = badge.dataset.tag;
        store.updateSettings({ tagFilter: tag });
        showToast(`Filtrando por "${tag}"`, "info");
      };
    });

    container.appendChild(card);
  });

  if (typeof onLoadOlder === "function") {
    const loadMoreRow = document.createElement("div");
    loadMoreRow.className = "load-more-row";
    loadMoreRow.innerHTML = `
      <button class="btn btn-secondary btn-load-more" id="btn-load-more-footer">
        <span class="btn-icon">⏳</span> Cargar episodios más antiguos
      </button>
    `;
    const footerBtn = loadMoreRow.querySelector("#btn-load-more-footer");
    if (footerBtn) {
      footerBtn.onclick = () => onLoadOlder(footerBtn);
    }
    container.appendChild(loadMoreRow);
  }
}

/**
 * Update dropdown values and options based on store state
 */
export function updateFilterDropdowns() {
  const channelSelect = document.getElementById("select-filter-channel");
  const dateSelect = document.getElementById("select-filter-date");
  const durSelect = document.getElementById("select-filter-duration");
  const statusSelect = document.getElementById("select-filter-status");
  const tagSelect = document.getElementById("select-filter-tag");
  const resetBtn = document.getElementById("btn-reset-filters-bar");

  // Populate channels if select exists
  if (channelSelect) {
    const subs = store.getSubscriptions().filter((s) => s.enabled !== false);
    const currentVal = store.settings.activeChannelId || "all";
    channelSelect.innerHTML = `<option value="all">📺 Todos los canales (${subs.length})</option>`;
    subs.forEach((sub) => {
      const opt = document.createElement("option");
      opt.value = sub.id;
      opt.textContent = sub.title;
      channelSelect.appendChild(opt);
    });
    channelSelect.value = currentVal;
    channelSelect.classList.toggle("is-active", currentVal !== "all");
  }

  // Date select
  if (dateSelect) {
    const val = store.settings.dateFilter || "all";
    dateSelect.value = val;
    dateSelect.classList.toggle("is-active", val !== "all");
  }

  // Duration select
  if (durSelect) {
    const val = store.settings.durationFilter || "all";
    durSelect.value = val;
    durSelect.classList.toggle("is-active", val !== "all");
  }

  // Status select
  if (statusSelect) {
    const val = store.settings.statusFilter || "unheard";
    statusSelect.value = val;
    statusSelect.classList.toggle("is-active", val !== "unheard");
  }

  // Tag select
  if (tagSelect) {
    const usedTags = store.getAllUsedTags();
    const currentTag = store.settings.tagFilter || "all";
    tagSelect.innerHTML = `<option value="all">🏷️ Todas las etiquetas</option>`;
    if (usedTags.length > 0) {
      usedTags.forEach((t) => {
        const opt = document.createElement("option");
        opt.value = t;
        opt.textContent = t;
        tagSelect.appendChild(opt);
      });
    }
    tagSelect.value = currentTag;
    tagSelect.classList.toggle("is-active", currentTag !== "all");
  }

  const hasActiveFilters =
    (store.settings.activeChannelId && store.settings.activeChannelId !== "all") ||
    (store.settings.dateFilter && store.settings.dateFilter !== "all") ||
    (store.settings.durationFilter && store.settings.durationFilter !== "all") ||
    (store.settings.statusFilter && store.settings.statusFilter !== "unheard") ||
    (store.settings.tagFilter && store.settings.tagFilter !== "all") ||
    store.settings.hideShorts === false;

  if (resetBtn) {
    resetBtn.classList.toggle("hidden", !hasActiveFilters);
  }
  document
    .getElementById("btn-toggle-filters")
    ?.classList.toggle("has-active-filters", Boolean(hasActiveFilters));
}

/**
 * Update bottom audio player dock UI
 */
export function updatePlayerDock(playerCtrl) {
  const dock = document.getElementById("player-dock");
  if (!dock) return;

  const current = playerCtrl.currentVideo;
  if (!current) {
    dock.classList.remove("is-active");
    return;
  }

  dock.classList.add("is-active");

  // Metadata
  const thumbEl = document.getElementById("player-dock-thumb");
  const titleEl = document.getElementById("player-dock-title");
  const channelEl = document.getElementById("player-dock-channel");
  const playBtn = document.getElementById("player-dock-play");
  const speedBtn = document.getElementById("player-dock-speed");
  const timeCurrentEl = document.getElementById("player-time-current");
  const timeTotalEl = document.getElementById("player-time-total");
  const progressFillEl = document.getElementById("player-progress-fill");
  const scrubSlider = document.getElementById("player-scrub-slider");
  const listenedBtn = document.getElementById("player-dock-listened");
  const externalLink = document.getElementById("player-dock-external");

  if (thumbEl) thumbEl.src = current.thumbnail;
  if (titleEl) titleEl.textContent = current.title;
  if (channelEl) channelEl.textContent = current.channelTitle || current.author;

  if (externalLink) {
    externalLink.href = current.link || `https://www.youtube.com/watch?v=${current.videoId}`;
    externalLink.title = current.audioUrl
      ? "Abrir publicación / podcast original"
      : "Abrir en YouTube / Brave";
  }

  // Play/pause icon
  if (playBtn) {
    playBtn.innerHTML = playerCtrl.isPlaying
      ? `
      <svg viewBox="0 0 24 24" class="icon"><rect x="6" y="4" width="4" height="16"></rect><rect x="14" y="4" width="4" height="16"></rect></svg>
    `
      : `
      <svg viewBox="0 0 24 24" class="icon"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
    `;
    playBtn.setAttribute("aria-label", playerCtrl.isPlaying ? "Pausar" : "Reproducir");
  }

  // Speed
  if (speedBtn) {
    speedBtn.textContent = `${playerCtrl.playbackRate}x`;
  }

  // Timeline
  const currentSec = playerCtrl.currentTime || 0;
  const totalSec = playerCtrl.duration || 0;
  if (timeCurrentEl) timeCurrentEl.textContent = formatDuration(currentSec) || "0:00";
  if (timeTotalEl) timeTotalEl.textContent = formatDuration(totalSec) || "--:--";

  const percent = totalSec > 0 ? (currentSec / totalSec) * 100 : 0;
  if (progressFillEl) progressFillEl.style.width = `${percent}%`;
  if (scrubSlider && !scrubSlider.matches(":active")) {
    scrubSlider.value = percent;
  }

  // Mark as listened button in player
  if (listenedBtn) {
    const isListened = store.isListened(current.videoId);
    listenedBtn.classList.toggle("active", isListened);
    listenedBtn.title = isListened ? "Marcado como escuchado" : "Marcar como escuchado";
  }

  // Theater modal visibility & mode
  const theaterModal = document.getElementById("theater-modal");
  if (theaterModal) {
    theaterModal.classList.toggle("is-open", playerCtrl.isExpanded);

    const ytContainer = document.getElementById("yt-player-container");
    const audioContainer = document.getElementById("audio-player-container");
    const audioArtwork = document.getElementById("audio-player-artwork");
    const audioTitle = document.getElementById("audio-player-title");
    const audioHost = document.getElementById("audio-player-host");
    const audioBadge = document.getElementById("audio-player-badge");

    if (playerCtrl.isAudioMode) {
      if (ytContainer) ytContainer.classList.add("hidden");
      if (audioContainer) {
        audioContainer.classList.remove("hidden");
        if (audioArtwork) audioArtwork.src = current.thumbnail || "./icons/icon-512.png";
        if (audioTitle) audioTitle.textContent = current.title;
        if (audioHost) audioHost.textContent = current.channelTitle || current.author || "";
        if (audioBadge) {
          const isSub =
            current.sourceType === "substack" || current.channelId?.includes("substack");
          audioBadge.textContent = isSub ? "Substack Audio" : "Podcast Audio";
          audioBadge.className = `badge-source-tag ${isSub ? "badge-source-substack" : "badge-source-rss"}`;
        }
      }
    } else {
      if (ytContainer) ytContainer.classList.remove("hidden");
      if (audioContainer) audioContainer.classList.add("hidden");
    }
  }
}

/**
 * Fast in-place update for episode cards active playback state without full grid re-render
 */
export function updateActiveCardState(currentVideoId) {
  const cards = document.querySelectorAll(".episode-card");
  cards.forEach((card) => {
    const isCurrent = currentVideoId ? card.id === `card-${currentVideoId}` : false;
    card.classList.toggle("is-current", isCurrent);
  });
}
